import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import StyledQRCode from '../components/StyledQRCode';
import { apiCall } from '../utils/api';

const COLORS = ['#626ed4', '#02a499', '#f8b425', '#6f42c1', '#ec4561'];

function QRDetail({ user, onLogout }) {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const [qr, setQr] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [scans, setScans] = useState([]);
  const [scansTotal, setScansTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, [qrId]);

  const fetchData = async () => {
    try {
      const [qrRes, analyticsRes, scansRes] = await Promise.all([
        apiCall(`/api/qrcodes/${qrId}`, {  }),
        apiCall(`/api/qrcodes/${qrId}/analytics`, {  }),
        apiCall(`/api/qrcodes/${qrId}/scans?limit=20&skip=0`, {  })
      ]);

      const qrData = await qrRes.json();
      const analyticsData = await analyticsRes.json();
      const scansData = await scansRes.json();

      setQr(qrData);
      setAnalytics(analyticsData);
      setScans(scansData.scans || []);
      setScansTotal(scansData.total || 0);
    } catch (error) {
      toast.error('Failed to load QR code');
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = async (format) => {
    try {
      const response = await apiCall(
        `/api/qrcodes/${qrId}/download?format=${format}&size=${qr.customization.size || 400}`
      );
      
      if (!response.ok) {
        toast.error(`Download failed: Error ${response.status}`);
        return;
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_${qr.short_code}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('[Download] Error:', error);
      toast.error(`Download failed: ${error.message || 'Network error'}`);
    }
  };

  const exportScans = async () => {
    try {
      const response = await apiCall(
        `/api/qrcodes/${qrId}/export-scans`
      );
      
      if (!response.ok) {
        toast.error(`Export failed: Error ${response.status}`);
        return;
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_${qr.short_code}_scans.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Scan data exported to CSV');
    } catch (error) {
      console.error('[Export] Error:', error);
      toast.error(`Export failed: ${error.message || 'Network error'}`);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  const qrValue = `${window.location.origin}/r/${qr.short_code}`;

  const deviceData = Object.keys(analytics.scansByDeviceType || {}).map(key => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value: analytics.scansByDeviceType[key]
  }));

  const browserData = Object.keys(analytics.scansByBrowser || {}).map(key => ({
    name: key,
    value: analytics.scansByBrowser[key]
  })).slice(0, 5);

  const osData = Object.keys(analytics.scansByOS || {}).map(key => ({
    name: key,
    value: analytics.scansByOS[key]
  })).slice(0, 5);

  const dateData = Object.keys(analytics.scansByDate || {}).sort().map(date => ({
    date: new Date(date).toLocaleDateString(),
    scans: analytics.scansByDate[date]
  }));

  const countryData = Object.keys(analytics.scansByCountry || {}).map(key => ({
    name: key,
    scans: analytics.scansByCountry[key]
  })).sort((a, b) => b.scans - a.scans).slice(0, 5);

  const hourData = Object.keys(analytics.scansByHour || {}).sort((a, b) => parseInt(a) - parseInt(b)).map(hour => ({
    hour: `${hour}:00`,
    scans: analytics.scansByHour[hour]
  }));

  const referrerData = Object.keys(analytics.scansByReferrer || {}).map(key => ({
    name: key === 'None' || key === 'null' ? 'Direct' : key,
    value: analytics.scansByReferrer[key]
  })).sort((a, b) => b.value - a.value).slice(0, 5);

  return (
    <div className="app-layout">
      <Sidebar onLogout={onLogout} user={user} />
      
      <div className="main-content">
        <Topbar user={user} pageTitle="QR Analytics" />
        
        <div className="page-content">
          <nav className="breadcrumb">
            <span className="breadcrumb-item" onClick={() => navigate('/projects')} style={{ cursor: 'pointer' }}>Projects</span>
            <span className="breadcrumb-item" onClick={() => navigate(`/projects/${qr.project_id}`)} style={{ cursor: 'pointer' }}>Project</span>
            <span className="breadcrumb-item active">{qr.name}</span>
          </nav>

          <div className="row mb-4">
            <div className="col-lg-8">
              <div className="card">
                <h4 className="mb-2">{qr.name}</h4>
                <p className="text-muted mb-3" style={{ wordBreak: 'break-all' }}>{qr.destination_url}</p>
                <div className="short-link-box">
                  <div style={{ flex: 1 }}>
                    <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>Short Link</p>
                    <p className="mb-0" style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{qrValue}</p>
                  </div>
                  <button className="btn btn-light btn-sm" onClick={() => navigator.clipboard.writeText(qrValue).then(() => toast.success('Copied!'))}>
                    📋 Copy
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card text-center">
                <div style={{ background: 'var(--gray-100)', padding: '1.5rem', borderRadius: 'var(--border-radius)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  <StyledQRCode
                    value={qrValue}
                    size={180}
                    fgColor={qr.customization?.fg_color || '#000000'}
                    bgColor={qr.customization?.bg_color || '#FFFFFF'}
                    pattern={qr.customization?.pattern || 'squares'}
                    cornerStyle={qr.customization?.corner_style || 'square'}
                    logoUrl={qr.customization?.logo_file_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${qr.customization.logo_file_id}` : null}
                    logoSize={(qr.customization?.logo_size || 20) / 100}
                    frameStyle={qr.customization?.frame_style || 'none'}
                    frameText={qr.customization?.frame_text || 'SCAN ME'}
                    frameColor={qr.customization?.frame_color || '#626ed4'}
                  />
                </div>
                <div className="d-flex gap-2 mb-2">
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => downloadQR('png')} data-testid="download-png">📥 PNG</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => downloadQR('svg')} data-testid="download-svg">📥 SVG</button>
                </div>
                <button className="btn btn-success w-100" onClick={exportScans} data-testid="export-csv">📊 Export CSV</button>
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="stat-card">
                <div className="stat-card-icon">👁️</div>
                <h3>{analytics.totalScans}</h3>
                <p>Total Scans</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-card success">
                <div className="stat-card-icon">🌍</div>
                <h3>{Object.keys(analytics.scansByCountry || {}).length}</h3>
                <p>Countries</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-card info">
                <div className="stat-card-icon">📱</div>
                <h3>{Object.keys(analytics.scansByDeviceType || {}).length}</h3>
                <p>Device Types</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-card warning">
                <div className="stat-card-icon">🔗</div>
                <h3>{Object.keys(analytics.scansByReferrer || {}).length}</h3>
                <p>Traffic Sources</p>
              </div>
            </div>
          </div>

          <div className="nav-tabs mb-4">
            <button className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>📊 Overview</button>
            <button className={`nav-tab ${activeTab === 'devices' ? 'active' : ''}`} onClick={() => setActiveTab('devices')}>📱 Devices</button>
            <button className={`nav-tab ${activeTab === 'location' ? 'active' : ''}`} onClick={() => setActiveTab('location')}>🌍 Location</button>
            <button className={`nav-tab ${activeTab === 'traffic' ? 'active' : ''}`} onClick={() => setActiveTab('traffic')}>🔗 Traffic</button>
            <button className={`nav-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>📋 History</button>
          </div>

          {analytics.totalScans === 0 ? (
            <div className="card empty-state">
              <div className="empty-state-icon">📈</div>
              <h3>No scans yet</h3>
              <p className="text-muted">Share your QR code to start collecting analytics</p>
            </div>
          ) : (
            <div>
              {activeTab === 'overview' && (
                <div className="row">
                  <div className="col-lg-6 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Scans Over Time</h5>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dateData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis dataKey="date" style={{ fontSize: '0.75rem' }} />
                          <YAxis style={{ fontSize: '0.75rem' }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="scans" stroke="#626ed4" strokeWidth={3} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="col-lg-6 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Scans by Time of Day</h5>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={hourData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis dataKey="hour" style={{ fontSize: '0.75rem' }} />
                          <YAxis style={{ fontSize: '0.75rem' }} />
                          <Tooltip />
                          <Bar dataKey="scans" fill="#6f42c1" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="col-lg-4 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Device Distribution</h5>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={deviceData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                            {deviceData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="col-lg-4 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Top Browsers</h5>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={browserData} layout="horizontal">
                          <XAxis type="number" style={{ fontSize: '0.75rem' }} />
                          <YAxis dataKey="name" type="category" width={70} style={{ fontSize: '0.75rem' }} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#02a499" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="col-lg-4 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Operating Systems</h5>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={osData} layout="horizontal">
                          <XAxis type="number" style={{ fontSize: '0.75rem' }} />
                          <YAxis dataKey="name" type="category" width={70} style={{ fontSize: '0.75rem' }} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#38a4f8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'devices' && (
                <div className="row">
                  <div className="col-lg-4 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Device Distribution</h5>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie data={deviceData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={5} dataKey="value">
                            {deviceData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="d-flex justify-content-center gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
                        {deviceData.map((entry, index) => (
                          <div key={index} className="d-flex align-items-center gap-1">
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS[index % COLORS.length] }} />
                            <span style={{ fontSize: '0.875rem' }}>{entry.name} ({entry.value})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Top Browsers</h5>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={browserData} layout="horizontal">
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis type="number" style={{ fontSize: '0.75rem' }} />
                          <YAxis dataKey="name" type="category" width={80} style={{ fontSize: '0.75rem' }} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#02a499" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="col-lg-4 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Operating Systems</h5>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={osData} layout="horizontal">
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis type="number" style={{ fontSize: '0.75rem' }} />
                          <YAxis dataKey="name" type="category" width={80} style={{ fontSize: '0.75rem' }} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#38a4f8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'location' && (
                <div className="row">
                  <div className="col-lg-12 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Geographic Distribution</h5>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={countryData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis dataKey="name" style={{ fontSize: '0.75rem' }} />
                          <YAxis style={{ fontSize: '0.75rem' }} />
                          <Tooltip />
                          <Bar dataKey="scans" fill="#626ed4" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'traffic' && (
                <div className="row">
                  <div className="col-lg-12 mb-4">
                    <div className="chart-container">
                      <h5 className="chart-title">Traffic Sources</h5>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={referrerData}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis dataKey="name" style={{ fontSize: '0.75rem' }} />
                          <YAxis style={{ fontSize: '0.75rem' }} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#f8b425" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <div className="card">
                  <h5 className="mb-3">Recent Scans ({scansTotal})</h5>
                  {scans.length === 0 ? (
                    <div className="empty-state">
                      <p className="text-muted">No scan records yet</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Location</th>
                            <th>Device</th>
                            <th>Browser</th>
                            <th>OS</th>
                            <th>IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scans.map((scan, index) => (
                            <tr key={scan.id || index}>
                              <td>{scan.timestamp ? new Date(scan.timestamp).toLocaleString() : 'N/A'}</td>
                              <td>{scan.city && scan.country ? `${scan.city}, ${scan.country}` : scan.country || 'Unknown'}</td>
                              <td><span className="badge badge-info">{scan.device_type || 'Unknown'}</span></td>
                              <td>{scan.browser || 'Unknown'}</td>
                              <td>{scan.os || 'Unknown'}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{scan.ip_address || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QRDetail;
